import {useAddTherapyToPatient} from '@/modules/patients/hooks/use-add-therapy-to-patient'
import {useTherapyFlatRows} from '@/modules/patients/hooks/use-therapy-flat-rows'
import {buildPrintRows, getSelectableRowIds} from '@/modules/patients/therapy-selection'
import {PatientTherapyProvider, usePatientTherapyContext} from '@/shared/contexts'
import {useCan} from '@/shared/hooks/use-permissions'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {openPdfBlob} from '@/shared/utils/open-pdf'
import {Badge, Box, Button, Separator, Tabs, Text} from '@chakra-ui/react'
import {PatientSource} from '@cypro/engine/database/schema'
import {Patient} from '@cypro/engine/schema'
import {dialog} from '@cypro/ui/components'
import {pdf} from '@react-pdf/renderer'
import {useTranslate} from '@tolgee/react'
import {useCallback, useMemo, useRef, useState} from 'react'
import {LuDivide, LuEye, LuEyeOff, LuListPlus, LuPlus, LuPrinter, LuX} from 'react-icons/lu'
import {CumulatedDoseContent} from './cumulated-dose'
import {AddDoseReductionDialog} from './dialogs/dose-reduction/add-dose-reduction-dialog'
import {EmptyProtocolCard} from './empty-protocol-card'
import {ProtocolCard} from './protocol-card'
import {TherapyTableNew} from './tables/therapy-table'
import {TherapyTabsList} from './tabs-list'
import {TherapyTablePrint} from './therapy-table-print'

export function TherapyTab({patient}: {patient: Patient}) {
  const scrollableRef = useRef<HTMLDivElement | null>(null)

  return (
    <Tabs.Content
      value="therapy"
      display="flex"
      flexDirection="column"
      paddingTop="0"
      ref={scrollableRef}
      flex="1"
      minHeight="0"
      overflowY={'auto'}
      scrollbarGutter={'stable'}
    >
      <PatientTherapyProvider
        patientId={patient.id}
        isPendingHl7Import={patient.source === PatientSource.enum.hl7_message}
      >
        <Tabs.Root defaultValue="protocols" variant={'enclosed'} lazyMount unmountOnExit>
          <Box display="flex" width="100%" flexDirection="column" pt="4" gap="5">
            <Box display="flex" flexDirection="row" gap="4" justifyContent={'space-between'}>
              <Box display="flex" flexDirection="column" gap="2">
                <ProtocolsTabContent />
              </Box>
            </Box>
            <TherapyTabControls scrollableRef={scrollableRef} patient={patient} />
            <CumulatedDoseContent />
          </Box>
        </Tabs.Root>
      </PatientTherapyProvider>
    </Tabs.Content>
  )
}

function ProtocolsTabContent() {
  const {patientProtocols, selectedProtocolIdsState, setSelectedProtocolIdsState} = usePatientTherapyContext()

  function handleProtocolCheckedChange(protocolId: string, checked: boolean) {
    setSelectedProtocolIdsState(prev => {
      const ids = new Set(prev)
      if (checked) {
        ids.add(protocolId)
      } else {
        ids.delete(protocolId)
      }
      return Array.from(ids)
    })
  }
  return (
    <Box
      display="flex"
      flexDirection="row"
      width="calc(100vw - 8rem)"
      gap="2"
      overflowX="auto"
      scrollbarGutter={'stable'}
    >
      {patientProtocols.length > 0 ? (
        (patientProtocols ?? []).map(pt => (
          <ProtocolCard
            key={pt.id}
            patientProtocol={pt}
            checked={selectedProtocolIdsState.includes(String(pt.id))}
            onCheckedChange={checked => handleProtocolCheckedChange(pt.id, checked)}
          />
        ))
      ) : (
        <EmptyProtocolCard />
      )}
    </Box>
  )
}

function TherapyTabControls({
  scrollableRef,
  patient,
}: {
  scrollableRef: React.RefObject<HTMLDivElement | null>
  patient: Patient
}) {
  const {
    patientTherapies,
    setSelectedProtocolIdsState,
    patientId,
    hideIsolated,
    setPrescriptionsVisibility,
    hidePrevious,
    setPrescriptionsPreviousVisibility,
    hideNotes,
    setHideNotes,
    isPendingHl7Import,
    selectedTherapyIds,
    clearTherapySelection,
  } = usePatientTherapyContext()
  const {t} = useTranslate('common')
  const zero = useZero()
  const flatRows = useTherapyFlatRows()
  const canEditPrescriptions = useCan('prescriptions.edit')
  const [isPrinting, setIsPrinting] = useState(false)
  const printRows = useMemo(() => buildPrintRows(flatRows, selectedTherapyIds), [flatRows, selectedTherapyIds])

  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)

  const hasCurrentOrFutureTherapies = (patientTherapies ?? []).some(therapy => {
    return therapy.therapyDate >= startOfToday.getTime()
  })

  const handleProtocolCheckedChange = useCallback(
    (protocolId: string, checked: boolean) => {
      setSelectedProtocolIdsState(prev => {
        const ids = new Set(prev)
        if (checked) {
          ids.add(protocolId)
        } else {
          ids.delete(protocolId)
        }
        return Array.from(ids)
      })
    },
    [setSelectedProtocolIdsState]
  )

  const {addPatientProtocol, addPatientEntry} = useAddTherapyToPatient({
    patientId,
    onAddProtocol: id => handleProtocolCheckedChange(id, true),
  })

  async function onClickDoseReduction() {
    const map = new Map<string, string>()

    const currentOrFutureTherapies = (patientTherapies ?? []).filter(
      therapy => therapy.therapyDate >= startOfToday.getTime()
    )

    for (const therapy of currentOrFutureTherapies) {
      for (const med of therapy.medicine ?? []) {
        const drug = med.activeSubstance ?? med.prescribedName
        const id = drug?.id
        const name = drug?.name

        if (id && name && !map.has(id)) {
          map.set(id, name)
        }
      }
    }

    await dialog.open('add-dose-reduction', {
      children: (
        <AddDoseReductionDialog
          patientId={patientId}
          drugItems={Array.from(map.entries()).map(([id, name]) => ({id, name}))}
        />
      ),
    })
    dialog.close('add-dose-reduction', true)
  }

  async function onClickPrint() {
    setIsPrinting(true)

    try {
      const blob = await pdf(<TherapyTablePrint patient={patient} flatRows={printRows} t={t} />).toBlob()
      openPdfBlob(blob, t('pdf_popup_blocked'))

      const printedIds = selectedTherapyIds.size > 0 ? [...selectedTherapyIds] : getSelectableRowIds(flatRows)
      if (printedIds.length > 0) {
        try {
          zero.attemptMutation(zero.mutators.patients.logTherapyPrint({patientId, therapySequenceIds: printedIds}))
        } catch {
          // logging is best effort; the printout already opened
        }
      }
    } finally {
      setIsPrinting(false)
    }
  }

  return (
    <Box width="100%">
      <Box display="flex" flexDirection="row" justifyContent={'space-between'}>
        <Box display="flex" justifyContent={'center'}>
          <TherapyTabsList />
        </Box>
        <Box display="flex" flexDirection="row" gap="3" justifyContent={'flex-end'} alignItems={'center'}>
          <Button
            size="md"
            variant="plain"
            colorPalette="cyan"
            paddingInline="0"
            onClick={() => setPrescriptionsVisibility(!hideIsolated)}
          >
            {hideIsolated ? <LuEye /> : <LuEyeOff />}
            <Text fontSize="sm" fontWeight="medium">
              {hideIsolated ? t('show_isolated') : t('hide_isolated')}
            </Text>
          </Button>
          <Button
            size="md"
            variant="plain"
            colorPalette="cyan"
            paddingInline="0"
            onClick={() => setPrescriptionsPreviousVisibility(!hidePrevious)}
          >
            {hidePrevious ? <LuEye /> : <LuEyeOff />}
            <Text fontSize="sm" fontWeight="medium">
              {hidePrevious ? t('show_previous') : t('hide_previous')}
            </Text>
          </Button>
          <Button
            size="md"
            variant="plain"
            colorPalette="cyan"
            paddingInline="0"
            onClick={() => setHideNotes(!hideNotes)}
          >
            {hideNotes ? <LuEye /> : <LuEyeOff />}
            <Text fontSize="sm" fontWeight="medium">
              {hideNotes ? t('show_notes') : t('hide_notes')}
            </Text>
          </Button>
          <Separator orientation={'vertical'} background="border" height="80%" />
          <Button
            size="md"
            variant="plain"
            colorPalette="cyan"
            paddingInline="0"
            onClick={() => onClickDoseReduction()}
            disabled={!canEditPrescriptions || !hasCurrentOrFutureTherapies || isPendingHl7Import}
          >
            <LuDivide />
            <Text fontSize="sm" fontWeight="medium">
              {t('dose_reduction')}
            </Text>
          </Button>

          <Button
            size="md"
            variant="subtle"
            colorPalette="cyan"
            paddingInline="2.5"
            onClick={addPatientEntry}
            disabled={isPendingHl7Import || !canEditPrescriptions}
          >
            <LuListPlus />
            <Text fontSize="sm" fontWeight="medium">
              {t('therapy_sequence.no_drugs.add_entry')}
            </Text>
          </Button>

          <Button
            size="md"
            variant="subtle"
            colorPalette="cyan"
            paddingInline="2.5"
            onClick={onClickPrint}
            loading={isPrinting}
            disabled={printRows.length === 0}
          >
            <LuPrinter />
            <Text fontSize="sm" fontWeight="medium">
              {t('print')}
            </Text>
            {selectedTherapyIds.size > 0 && (
              <Badge colorPalette="cyan" variant="solid">
                {selectedTherapyIds.size}
              </Badge>
            )}
          </Button>

          {selectedTherapyIds.size > 0 && (
            <Button size="md" variant="plain" colorPalette="cyan" paddingInline="0" onClick={clearTherapySelection}>
              <LuX />
              <Text fontSize="sm" fontWeight="medium">
                {t('clear_selection')}
              </Text>
            </Button>
          )}

          <Button
            colorPalette={'cyan'}
            size="sm"
            onClick={addPatientProtocol}
            disabled={isPendingHl7Import || !canEditPrescriptions}
          >
            <LuPlus />
            {t('new_protocol')}
          </Button>
        </Box>
      </Box>
      <Tabs.Content value="protocols" display="flex" flexDirection="column" paddingTop="0" overflow="none">
        {scrollableRef.current && <TherapyTableNew scrollableRef={scrollableRef.current} />}
      </Tabs.Content>
    </Box>
  )
}
